import type {
  ChatEnvelope,
  ChatMessage,
  ErrorEnvelope,
} from '../types/envelope';

export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Erro de rede');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export class SessionExpiredError extends Error {
  constructor(
    public readonly reason: 'expired' | 'invalid' = 'expired',
  ) {
    super('Sessão expirada');
    this.name = 'SessionExpiredError';
  }
}

export class RateLimitedError extends Error {
  constructor(message = 'Muitas requisições') {
    super(message);
    this.name = 'RateLimitedError';
  }
}

export class ApiError extends Error {
  constructor(message: string, public readonly errorCode: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface AiApiServiceOptions {
  apiUrl: string;
  sessionToken: string;
}

export class AiApiService {
  constructor(private readonly opts: AiApiServiceOptions) {}

  async chat(message: string, history: ChatMessage[]): Promise<ChatEnvelope> {
    const url = `${this.opts.apiUrl.replace(/\/$/, '')}/chat`;
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          Authorization: `Bearer ${this.opts.sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message, history }),
      });
    } catch (err) {
      throw new NetworkError(err);
    }

    if (response.ok) {
      return (await response.json()) as ChatEnvelope;
    }

    const body = (await response.json().catch(() => null)) as ErrorEnvelope | null;

    if (response.status === 401) {
      const code = body?.error_code;
      throw new SessionExpiredError(code === 'SESSION_INVALID' ? 'invalid' : 'expired');
    }
    if (response.status === 429) {
      throw new RateLimitedError(body?.content ?? 'Muitas requisições');
    }
    throw new ApiError(
      body?.content ?? `HTTP ${response.status}`,
      body?.error_code ?? 'INTERNAL',
    );
  }

  async refreshSchema(): Promise<void> {
    const url = `${this.opts.apiUrl.replace(/\/$/, '')}/chat/schema/refresh`;
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          Authorization: `Bearer ${this.opts.sessionToken}`,
        },
      });
    } catch (err) {
      throw new NetworkError(err);
    }

    if (response.ok) return;

    if (response.status === 401) {
      throw new SessionExpiredError('expired');
    }
    const body = (await response.json().catch(() => null)) as ErrorEnvelope | null;
    throw new ApiError(
      body?.content ?? `HTTP ${response.status}`,
      body?.error_code ?? 'INTERNAL',
    );
  }
}
